import { Interest } from "@/schemas/profile/attributes/UserInterestAttributeSet";
import { Language } from "@/schemas/profile/attributes/UserLanguageAttributeSet";
import { Seeking } from "@/schemas/profile/attributes/UserSeekingAttributeSet";
import { Height } from "@/schemas/profile/attributes/UserHeightAttributeSet";
import {
  CompatibilityType,
  RecommendationCategory,
  RecommendationHighlightType,
  UserRecommendedItem,
} from "@/schemas/user/explore";
import { UserImageType } from "@/schemas/user/image";
import { Ethnicity } from "@/schemas/profile/attributes/UserEthnicityAttributeSet";
import { BodyType } from "@/schemas/profile/attributes/UserBodyAttributeSet";
import { Weight } from "@/schemas/profile/attributes/UserWeightAttributeSet";
import { RolePosition } from "@/schemas/profile/attributes/UserRoleAttributeSet";

/**
 * Category priority metadata for profiles
 * Maps profile IDs to the categories where they should be featured and their priority rank
 */
export type ProfileCategoryPriority = {
  profileId: string;
  categories: {
    category: RecommendationCategory;
    priority: number; // Lower number = higher priority (1 = first)
  }[];
};

/**
 * Category priorities for United States themed profiles
 * Defines which profiles should appear first in specific recommendation categories
 *
 * Available categories: TRENDING, DATING, FRIENDS, ONLINE_NOW, NEW_USERS, NEARBY
 */
export const US_PROFILE_CATEGORY_PRIORITIES: ProfileCategoryPriority[] = [
  {
    profileId: "MKT001", // cosmopolitan - San Francisco
    categories: [
      { category: RecommendationCategory.TRENDING, priority: 1 },
      { category: RecommendationCategory.FRIENDS, priority: 2 },
      { category: RecommendationCategory.NEW_USERS, priority: 1 },
    ],
  },
  {
    profileId: "MKT002", // unapologetic - NYC
    categories: [
      { category: RecommendationCategory.TRENDING, priority: 2 },
      { category: RecommendationCategory.FRIENDS, priority: 1 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 1 },
    ],
  },
  {
    profileId: "MKT003", // ironheart - LA
    categories: [
      { category: RecommendationCategory.DATING, priority: 2 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 2 },
    ],
  },
  {
    profileId: "MKT004", // aperturex - Chicago
    categories: [
      { category: RecommendationCategory.FRIENDS, priority: 3 },
      { category: RecommendationCategory.NEW_USERS, priority: 2 },
    ],
  },
  {
    profileId: "MKT006", // succulent - Boston
    categories: [
      { category: RecommendationCategory.FRIENDS, priority: 4 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 3 },
    ],
  },
  {
    profileId: "MKT007", // lonestar - Austin
    categories: [
      { category: RecommendationCategory.DATING, priority: 1 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 4 },
    ],
  },
  {
    profileId: "MKT008", // organizer - DC
    categories: [
      { category: RecommendationCategory.FRIENDS, priority: 5 },
      { category: RecommendationCategory.NEW_USERS, priority: 3 },
    ],
  },
  {
    profileId: "MKT010", // trailmix - Portland
    categories: [
      { category: RecommendationCategory.TRENDING, priority: 3 },
      { category: RecommendationCategory.DATING, priority: 3 },
    ],
  },
  {
    profileId: "MKT011", // sunstruck - Phoenix
    categories: [
      { category: RecommendationCategory.DATING, priority: 4 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 5 },
    ],
  },
  {
    profileId: "MKT013", // jazzbird - New Orleans
    categories: [
      { category: RecommendationCategory.NEARBY, priority: 1 },
      { category: RecommendationCategory.NEW_USERS, priority: 4 },
    ],
  },
];

/**
 * United States themed mock data - used for demos and presentations
 * Contains 13 polished profiles with professional images and complete attributes
 * Showcases the app at its best for marketing purposes
 * Represents diverse gay community experiences across major US cities
 */
export const MOCK_UNITED_STATES_EXPLORE_ITEMS: UserRecommendedItem[] = [
  {
    id: "MKT001",
    rank: 1,
    username: "cosmopolitan",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/01.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/01.webp",
      },
      id: "mkt-001",
      likeCount: 127,
      type: UserImageType.PUBLIC_IMAGE,
      username: "cosmopolitan",
    },
    matchingPercentage: 95,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 97,
      },
      {
        rank: 1,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 92,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.TRAVELING,
      Interest.FITNESS,
      Interest.EATING,
      Interest.PHOTOGRAPHY,
    ],
    distance: 2.5,
    profileAttributes: {
      aboutMe:
        "Castro kid through and through. Tech bro by day, brunch queen by weekend. You'll find me at Dolores Park with a bottle of rosé or front row at drag brunch. Ru Girl obsessed, chosen family over everything. Looking for someone to hit up Folsom with or just grab coffee at Philz.",
      birthday: "1995-06-15",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_0,
      weight: Weight.LBS_175,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH, Language.SPANISH],
      interests: [
        Interest.TRAVELING,
        Interest.FITNESS,
        Interest.EATING,
        Interest.PHOTOGRAPHY,
        Interest.HIKING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT002",
    rank: 2,
    username: "unapologetic",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/02.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/02.webp",
      },
      id: "mkt-002",
      likeCount: 203,
      type: UserImageType.PUBLIC_IMAGE,
      username: "unapologetic",
    },
    matchingPercentage: 92,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 94,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SAME_ETHNICITY,
      },
    ],
    commonInterests: [Interest.GAMING, Interest.FITNESS, Interest.EATING],
    distance: 0.8,
    profileAttributes: {
      aboutMe:
        "Hell's Kitchen transplant living my truth. Bottomless brunch at Industry Kitchen, late nights at Pieces and Therapy. Out since college, never looking back. Black and queer and not apologizing for taking up space. Let's build community, not just swipe right.",
      birthday: "1993-09-22",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.BLACK,
      height: Height.FT_5_11,
      weight: Weight.LBS_180,
      relationship: null,
      role: RolePosition.TOP,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.CHAT,
        Seeking.EVENTS,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.FRENCH],
      interests: [
        Interest.GAMING,
        Interest.FITNESS,
        Interest.EATING,
        Interest.PHOTOGRAPHY,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT003",
    rank: 3,
    username: "ironheart",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/03.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/03.webp",
      },
      id: "mkt-003",
      likeCount: 156,
      type: UserImageType.PUBLIC_IMAGE,
      username: "ironheart",
    },
    matchingPercentage: 89,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 91,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.RECENT_ACTIVITY,
      },
    ],
    commonInterests: [Interest.TRAVELING, Interest.EATING, Interest.FITNESS],
    distance: 15.3,
    profileAttributes: {
      aboutMe:
        "WeHo fitness trainer, proud Chicano, queer AF. Grew up in East LA, came out at 19, never looked back. Early morning gym sessions, late night tacos at Yuca's. Abbey happy hours, volunteering at LA Pride. Looking for a gym buddy who becomes something more.",
      birthday: "1996-03-10",
      bodyType: BodyType.MUSCULAR,
      ethnicity: Ethnicity.LATINO,
      height: Height.FT_5_9,
      weight: Weight.LBS_165,
      relationship: null,
      role: RolePosition.VERS_TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.GYM_BUDDY,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH, Language.SPANISH],
      interests: [
        Interest.FITNESS,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.HIKING,
        Interest.PHOTOGRAPHY,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT004",
    rank: 4,
    username: "aperturex",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/04.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/04.webp",
      },
      id: "mkt-004",
      likeCount: 189,
      type: UserImageType.PUBLIC_IMAGE,
      username: "aperturex",
    },
    matchingPercentage: 91,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 88,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 85,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.MANY_SHARED_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.PHOTOGRAPHY,
      Interest.TRAVELING,
      Interest.EATING,
      Interest.HIKING,
    ],
    distance: 3.2,
    profileAttributes: {
      aboutMe:
        "Boystown photographer documenting our community. Asian-American, queer, out and unapologetic. I shoot Pride parades, drag shows, and the quiet moments in between. Coffee dates at Sidetrack, dancing at Berlin, Sunday Funday at Roscoe's. Let's create something beautiful together.",
      birthday: "1994-11-08",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_10,
      weight: Weight.LBS_155,
      relationship: null,
      role: RolePosition.BOTTOM,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.CHINESE],
      interests: [
        Interest.PHOTOGRAPHY,
        Interest.TRAVELING,
        Interest.EATING,
        Interest.HIKING,
        Interest.CRAFTS,
        Interest.READING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT005",
    rank: 5,
    username: "bibliophile",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/05.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/05.webp",
      },
      id: "mkt-005",
      likeCount: 142,
      type: UserImageType.PUBLIC_IMAGE,
      username: "bibliophile",
    },
    matchingPercentage: 88,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 86,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_LANGUAGE,
      },
    ],
    commonInterests: [Interest.TRAVELING, Interest.READING, Interest.CRAFTS],
    distance: 8.7,
    profileAttributes: {
      aboutMe:
        "Capitol Hill creative through and through. Queer book club at Wildrose, volunteer organizer for Seattle Pride. Supporting queer-owned businesses isn't optional, it's who I am. Looking for intentional connections, not just hookups. Coffee first, feelings later?",
      birthday: "1992-07-19",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_1,
      weight: Weight.LBS_185,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.CHAT,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH, Language.GERMAN],
      interests: [
        Interest.TRAVELING,
        Interest.READING,
        Interest.CRAFTS,
        Interest.EATING,
        Interest.PHOTOGRAPHY,
        Interest.HIKING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT006",
    rank: 6,
    username: "succulent",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/06.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/06.webp",
      },
      id: "mkt-006",
      likeCount: 221,
      type: UserImageType.PUBLIC_IMAGE,
      username: "succulent",
    },
    matchingPercentage: 93,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 95,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 90,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SIMILAR_AGE,
      },
    ],
    commonInterests: [
      Interest.FITNESS,
      Interest.GAMING,
      Interest.EATING,
      Interest.TRAVELING,
    ],
    distance: 1.2,
    profileAttributes: {
      aboutMe:
        "South End software engineer and plant dad to 20+ green babies. Gay volleyball league on Thursdays, Club Café karaoke on Fridays. Side, sex-positive, 420-friendly. Looking for someone who gets that 'succulent' is both a plant type and a compliment.",
      birthday: "1995-04-25",
      bodyType: BodyType.TONED,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_11,
      weight: Weight.LBS_170,
      relationship: null,
      role: RolePosition.SIDE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.GYM_BUDDY,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.FITNESS,
        Interest.GAMING,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.GARDENING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT007",
    rank: 7,
    username: "lonestar",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/07.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/07.webp",
      },
      id: "mkt-007",
      likeCount: 178,
      type: UserImageType.PUBLIC_IMAGE,
      username: "lonestar",
    },
    matchingPercentage: 87,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 89,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
    ],
    commonInterests: [Interest.FITNESS, Interest.HIKING, Interest.TRAVELING],
    distance: 5.4,
    profileAttributes: {
      aboutMe:
        "South Congress regular, breakfast taco connoisseur. Texas-born, ATX-raised, came out under the pride flag at UT. Barton Springs swimmer, ACL festival veteran. Vers top who knows how to two-step and isn't afraid of commitment. Let's grab Torchy's then catch a show at Oilcan's.",
      birthday: "1997-02-14",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_2,
      weight: Weight.LBS_190,
      relationship: null,
      role: RolePosition.VERS_TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.EVENTS,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.FITNESS,
        Interest.HIKING,
        Interest.TRAVELING,
        Interest.EATING,
        Interest.MUSIC,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT008",
    rank: 8,
    username: "organizer",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/08.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/08.webp",
      },
      id: "mkt-008",
      likeCount: 245,
      type: UserImageType.PUBLIC_IMAGE,
      username: "organizer",
    },
    matchingPercentage: 94,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 96,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 91,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.READING,
      Interest.WRITING,
      Interest.EATING,
      Interest.TRAVELING,
    ],
    distance: 1.8,
    profileAttributes: {
      aboutMe:
        "Dupont Circle political organizer fighting the good fight. Howard grad, Black Lives Matter, and yes, Black queer lives especially. Happy hour at Nellie's, brunch at Annie's. Looking for someone politically woke who can handle deep convos and deeper feelings.",
      birthday: "1991-08-30",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.BLACK,
      height: Height.FT_5_10,
      weight: Weight.LBS_175,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.LGBTQ_COMMUNITY,
        Seeking.NETWORKING,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.READING,
        Interest.WRITING,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT009",
    rank: 9,
    username: "rhythm",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/09.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/09.webp",
      },
      id: "mkt-009",
      likeCount: 167,
      type: UserImageType.PUBLIC_IMAGE,
      username: "rhythm",
    },
    matchingPercentage: 90,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 92,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_LANGUAGE,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SAME_ETHNICITY,
      },
    ],
    commonInterests: [Interest.DANCING, Interest.EATING, Interest.TRAVELING],
    distance: 6.2,
    profileAttributes: {
      aboutMe:
        "Wynwood artist and salsa dancer. Cuban-American, bilingual, and bi-coastal energy. Came out to my abuela in Spanish, she loves me anyway. Painting murals by day, dancing at Palace and Club Space by night. Looking for a papi who can keep up on the dance floor.",
      birthday: "1998-05-12",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.LATINO,
      height: Height.FT_5_8,
      weight: Weight.LBS_150,
      relationship: null,
      role: RolePosition.BOTTOM,
      residence: null,
      seeking: [Seeking.DATES, Seeking.FRIENDS, Seeking.CHAT, Seeking.EVENTS],
      languages: [Language.ENGLISH, Language.SPANISH],
      interests: [
        Interest.DANCING,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.CRAFTS,
        Interest.MUSIC,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT010",
    rank: 10,
    username: "trailmix",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/10.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/10.webp",
      },
      id: "mkt-010",
      likeCount: 134,
      type: UserImageType.PUBLIC_IMAGE,
      username: "trailmix",
    },
    matchingPercentage: 86,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 87,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
    ],
    commonInterests: [Interest.HIKING, Interest.COOKING, Interest.CRAFTS],
    distance: 4.1,
    profileAttributes: {
      aboutMe:
        "Pearl District barista and outdoor enthusiast. PNW gay who traded LA sunshine for Oregon rain and never regretted it. Saturday hikes, Sunday farmers markets. Scandals regular, volunteer at Q Center. Looking for someone to explore Powell's Books with, then maybe explore each other.",
      birthday: "1996-10-05",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_11,
      weight: Weight.LBS_165,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.COFFEE_DATES,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.HIKING,
        Interest.COOKING,
        Interest.CRAFTS,
        Interest.READING,
        Interest.PHOTOGRAPHY,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT011",
    rank: 11,
    username: "sunstruck",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/11.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/11.webp",
      },
      id: "mkt-011",
      likeCount: 198,
      type: UserImageType.PUBLIC_IMAGE,
      username: "sunstruck",
    },
    matchingPercentage: 91,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 93,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.MANY_SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SIMILAR_AGE,
      },
    ],
    commonInterests: [
      Interest.FITNESS,
      Interest.HIKING,
      Interest.TRAVELING,
      Interest.PHOTOGRAPHY,
    ],
    distance: 7.8,
    profileAttributes: {
      aboutMe:
        "Desert rat who loves the heat. ASU alum, out since freshman year, never apologized for it. Rock climbing, pool parties, and Phoenix Pride veteran. Charlie's regular, knows every drag queen in town. Top looking for his bottom, but personality matters more than position.",
      birthday: "1994-07-28",
      bodyType: BodyType.TONED,
      ethnicity: Ethnicity.LATINO,
      height: Height.FT_5_10,
      weight: Weight.LBS_160,
      relationship: null,
      role: RolePosition.TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.GYM_BUDDY,
        Seeking.EVENTS,
      ],
      languages: [Language.ENGLISH, Language.SPANISH],
      interests: [
        Interest.FITNESS,
        Interest.HIKING,
        Interest.TRAVELING,
        Interest.PHOTOGRAPHY,
        Interest.EATING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT012",
    rank: 12,
    username: "alpine",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/12.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/12.webp",
      },
      id: "mkt-012",
      likeCount: 212,
      type: UserImageType.PUBLIC_IMAGE,
      username: "alpine",
    },
    matchingPercentage: 88,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 90,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.HIKING,
      Interest.FITNESS,
      Interest.TRAVELING,
      Interest.CYCLING,
    ],
    distance: 3.6,
    profileAttributes: {
      aboutMe:
        "Cap Hill mountain man. Colorado native, ski instructor, and proud member of the LGBTQ Ski Community. Après ski at X Bar, summers at Trade. Beard game strong, communication game stronger. Looking for a ski buddy who might become a life partner.",
      birthday: "1993-12-20",
      bodyType: BodyType.MUSCULAR,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_1,
      weight: Weight.LBS_185,
      relationship: null,
      role: RolePosition.TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.NETWORKING,
        Seeking.GYM_BUDDY,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.HIKING,
        Interest.FITNESS,
        Interest.TRAVELING,
        Interest.CYCLING,
        Interest.PHOTOGRAPHY,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT013",
    rank: 13,
    username: "jazzbird",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/13.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/02-united-states-theme/13.webp",
      },
      id: "mkt-013",
      likeCount: 187,
      type: UserImageType.PUBLIC_IMAGE,
      username: "jazzbird",
    },
    matchingPercentage: 89,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 88,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.RECENT_ACTIVITY,
      },
    ],
    commonInterests: [Interest.MUSIC, Interest.EATING, Interest.DANCING],
    distance: 2.3,
    profileAttributes: {
      aboutMe:
        "French Quarter musician living for jazz and jambalaya. Southern hospitality meets queer sensibility. Bourbon Pub weekends, Southern Decadence every year. Vers bottom who can cook gumbo and discuss Foucault. Let's make music together, literally or metaphorically.",
      birthday: "1995-09-17",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_9,
      weight: Weight.LBS_145,
      relationship: null,
      role: RolePosition.VERS_BOTTOM,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.CHAT,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.FRENCH],
      interests: [
        Interest.MUSIC,
        Interest.EATING,
        Interest.DANCING,
        Interest.TRAVELING,
        Interest.READING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
];

export function getUnitedStatesExploreItems(): UserRecommendedItem[] {
  return [...MOCK_UNITED_STATES_EXPLORE_ITEMS];
}
